import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "src/store";

import { ConfirmDialog, DropdownItem } from "@devices/components/shared";
import DropdownContainer from "@devices/components/shared/Dropdown/DropdownContainer";
import { updateGroupMembers } from "@devices/store";
import { Group } from "@devices/types";
import DeleteGroupDialog from "@groups/components/DeleteGroupDialog";
import { useGroupDeleteGate } from "@groups/hooks/useGroupDeleteGate";
import { DialogContentText } from "@mui/material";
import AuthService from "@services/authService";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";

const DeleteGroupItem = (props: Group & { setIsDeleteGroupDialogOpen: (open: boolean) => void }) => {
    const { t } = useTranslation(["common"]);

    const { isDeleteDisabled, blockedReason } = useGroupDeleteGate(props.id);

    const handleDeleteGroup = () => {
        props.setIsDeleteGroupDialogOpen(true);
    };

    return (
        <DropdownItem
            disabled={isDeleteDisabled}
            iconName="fa-solid fa-fw fa-trash"
            label={t("common:button.delete")}
            onClick={handleDeleteGroup}
            tooltipProps={{ title: blockedReason }}
        />
    );
};

const LeaveGroupItem = (props: Group & { setIsLeaveGroupDialogOpen: (open: boolean) => void }) => {
    const { t } = useTranslation(["sidepanels", "modals", "common"]);
    const currentUser = AuthService.getCurrentUser();

    const isGroupMember = props.members.some((item) => item.email === currentUser?.email);

    const handleLeaveGroup = () => {
        props.setIsLeaveGroupDialogOpen(true);
    };

    if (!isGroupMember) {
        return (
            <DropdownItem
                label={t("sidepanels:groupPanel.button.leaveGroup")}
                iconName="fa-solid fa-right-from-bracket"
                tooltipProps={{ title: t("sidepanels:groupPanel.tooltip.notDirectMemberOfGroup") }}
                disabled
            />
        );
    }

    return (
        <DropdownItem
            label={t("sidepanels:groupPanel.button.leaveGroup")}
            iconName="fa-solid fa-right-from-bracket"
            onClick={handleLeaveGroup}
        />
    );
};

export default (props: Group): React.JSX.Element => {
    const [isLeaveGroupDialogOpen, setIsLeaveGroupDialogOpen] = React.useState(false);
    const [isDeleteGroupDialogOpen, setIsDeleteGroupDialogOpen] = React.useState(false);

    const anchorRef = React.useRef<HTMLButtonElement | null>(null);

    const dispatch = useDispatch();
    const currentUser = AuthService.getCurrentUser();
    const { t } = useTranslation(["sidepanels", "modals", "common"]);

    const cancelLeaveGroupDialog = () => {
        if (isLeaveGroupDialogOpen) {
            setIsLeaveGroupDialogOpen(false);
        }
    };
    const leaveGroup = async () => {
        if (currentUser) {
            await dispatch(updateGroupMembers({ id: props.id, removedMembers: [currentUser?.email] }))
                .unwrap()
                .then(() => showSuccessToast(t("sidepanels:groupPanel.toast.memberUpdated")))
                .catch(({ message }) => showErrorToast(message));
        }
    };

    return (
        <React.Fragment>
            <DropdownContainer icon={<i className="fa-solid fa-ellipsis-vertical" />} anchorRef={anchorRef}>
                <DeleteGroupItem {...props} setIsDeleteGroupDialogOpen={setIsDeleteGroupDialogOpen} />
                <LeaveGroupItem {...props} setIsLeaveGroupDialogOpen={setIsLeaveGroupDialogOpen} />
            </DropdownContainer>

            <DeleteGroupDialog group={props} open={isDeleteGroupDialogOpen} onClose={() => setIsDeleteGroupDialogOpen(false)} />

            <ConfirmDialog
                title={t("modals:leaveGroup.title")}
                open={isLeaveGroupDialogOpen}
                onConfirm={leaveGroup}
                onClose={cancelLeaveGroupDialog}
            >
                <DialogContentText>{t("modals:leaveGroup.description")}</DialogContentText>
            </ConfirmDialog>
        </React.Fragment>
    );
};
